import { ref } from 'vue'

import { defineStore, acceptHMRUpdate } from 'pinia'

import * as Sentry from "@sentry/vue";

import { useNProgress } from '@vueuse/integrations/useNProgress'

import { useJobStore } from './job'
import { useClientStore } from './client'
import { useCaregiverStore } from './caregiver'
import { getClientById } from './Api/client'
import { getCaregiverById } from './Api/caregiver'

export const useRatingStore = defineStore('rating', () => {

    const { isLoading } = useNProgress()
    const loading = ref(isLoading)



    //
    // API
    //

    async function store(job_id, payload) {

        loading.value = true

        return await new Promise((resolve, reject) => {
            axios.post('/api/v1/jobs/' + job_id + '/ratings', payload)
            .then(function (response) {
                // Handle redirect here
                if (response.status === 201) {
                    const redirectUrl = response.headers.location;
                    return axios.get(redirectUrl);
                }
            }).then(function (response) {
                loading.value = false
                resolve(response)
            }).catch(function (error) {
                loading.value = false
                reject({ "error": error })
                Sentry.captureException(error)
            })
        });

    }



    //
    // Page state sync
    //

    /**
     * Mirrors a created or edited rating into the job, client and caregiver stores, so feedback tables stay
     * current without a reload: the job's ratings (oldest first) and each party's latest_rating_per_job.
     * Neither can drop a job from the capped list wrongly: an edit keeps its ID and job, and a created rating is
     * the newest, so its job becomes the last entry and the bar's last ten push out the oldest job. If a reload
     * from a delete is still in flight for a party, it is reissued so the server state includes this rating.
     */
    function applySavedRating(rating) {
        const job = useJobStore().jobs[rating?.job_id]
        if (!Array.isArray(job?.ratings)) {
            return
        }
        const position = job.ratings.findIndex((item) => sameId(item.id, rating.id))
        if (position >= 0) {
            job.ratings.splice(position, 1, rating)
        } else {
            insertById(job.ratings, rating)
        }
        syncLatestRating(job, rating.job_id)
        for (const owner of latestRatingOwners(job).filter((item) => pendingReloads.has(item.key))) {
            reloadLatestRatings(owner)
        }
    }

    /**
     * Removes a deleted rating from the job, client and caregiver stores. Each party's latest rating for the job
     * optimistically falls back to the job's previous rating, then the party's latest_rating_per_job is reloaded
     * from the server: the API caps it at ten jobs, so an eleventh job may need to enter the bar.
     *
     * @returns {Promise<void>} Settles once the reloads finish; never rejects.
     */
    function applyRemovedRating(jobId, ratingId) {
        const job = useJobStore().jobs[jobId]
        if (!Array.isArray(job?.ratings)) {
            return Promise.resolve()
        }
        const position = job.ratings.findIndex((item) => sameId(item.id, ratingId))
        if (position >= 0) {
            job.ratings.splice(position, 1)
        }
        syncLatestRating(job, jobId)
        return Promise.all(latestRatingOwners(job).map(reloadLatestRatings)).then(() => undefined)
    }

    /**
     * Reload sequence per owner key; a response is only applied if no newer reload for that owner was issued.
     *
     * @type {Map<string, number>}
     */
    const pendingReloads = new Map()
    let reloadSequence = 0

    /**
     * @param {{ key: string, fetch: Function, owner: { latest_rating_per_job: Array } }} target
     */
    async function reloadLatestRatings({ key, fetch, owner }) {
        const sequence = ++reloadSequence
        pendingReloads.set(key, sequence)
        try {
            const response = await fetch(['latest_rating_per_job'])
            const latest = response?.data?.latest_rating_per_job
            if (pendingReloads.get(key) === sequence && Array.isArray(latest)) {
                const ascending = [...latest].sort((a, b) => Number(a.id) - Number(b.id))
                owner.latest_rating_per_job.splice(0, owner.latest_rating_per_job.length, ...ascending)
            }
        } catch (error) {
            Sentry.captureException(error)
        } finally {
            if (pendingReloads.get(key) === sequence) {
                pendingReloads.delete(key)
            }
        }
    }

    function sameId(a, b) {
        return a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b)
    }

    /**
     * Inserts a rating while keeping the list in ascending rating-ID order, as the API returns it; the rating bar
     * shows the last ten entries, so appending an older rating would push newer feedback out of view.
     */
    function insertById(list, rating) {
        const position = list.findIndex((item) => Number(item.id) > Number(rating.id))
        list.splice(position >= 0 ? position : list.length, 0, rating)
    }

    /**
     * The job's client and caregiver that are present in the stores with a latest_rating_per_job list.
     */
    function latestRatingOwners(job) {
        const clientId = job.client?.id
        const caregiverId = job.caregiver?.id
        return [
            { key: 'client:' + clientId, owner: useClientStore().clients[clientId], fetch: (fields) => getClientById(clientId, fields) },
            { key: 'caregiver:' + caregiverId, owner: useCaregiverStore().caregivers[caregiverId], fetch: (fields) => getCaregiverById(caregiverId, fields) },
        ].filter((item) => Array.isArray(item.owner?.latest_rating_per_job))
    }

    function syncLatestRating(job, jobId) {
        const latest = job.ratings.slice(-1)[0] ?? null
        for (const { owner } of latestRatingOwners(job)) {
            replaceLatestRating(owner, jobId, latest)
        }
    }

    function replaceLatestRating(owner, jobId, latest) {
        const position = owner.latest_rating_per_job.findIndex((item) => sameId(item.job_id, jobId))
        if (position >= 0) {
            owner.latest_rating_per_job.splice(position, 1)
        }
        if (latest) {
            insertById(owner.latest_rating_per_job, latest)
        }
    }

    return {
        store,
        applySavedRating,
        applyRemovedRating,
        loading
    }

})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useRatingStore, import.meta.hot))
}
